import clsx from "clsx";
import { Plus, Boxes } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearch } from "wouter";
import { basisLabel, rateFor } from "../../shared/money";
import { plural } from "../../shared/words";
import { Notice, PageHeader, Panel } from "../../web/components/ui";
import { useCompany } from "../../web/lib/company";
import { useMoney } from "../../web/lib/money";
import { rowOpen } from "../../web/lib/rowOpen";
import { bundleItemAttention, bundleMakesTooLittle, bundleNumberProblem, bundleProblems, type ItemProblem, itemContext, retiredInBundle } from "./attention";
import { type BundleTotals, bundleTotals } from "./bundleTotals";
import { can } from "./data";
import { ANY_KIND, FilterBar, matchesSearch, TagFilter } from "./ListToolbar";
import { CheckedListMark, type CheckedFilter, CheckedOnly, inCheckedFilter } from "./CheckedMark";
import { ListName } from "./ListName";
import type { NameTagConfig } from "./names";
import { ItemNumberCell, MarkupCell, TotalCell } from "./MaterialsList";
import { type BundleRow, type CheckedMark, isProduct, type MaterialRow, type PriceListData } from "./meta";
import { underMinimum } from "./pricing";
import { itemNumberIssues } from "./setup";
import { smartName } from "./smartTags";

/*
 * The Bundles tab, as ServiceM8 lists them beside its materials: each bundle, how many items it holds, and what they add up to
 * (a bundle has no price of its own: ServiceM8 adds each item to the job at its own price). Searching an item's name or number
 * finds the bundles holding it. Clicking a row opens it (BundleWindow.tsx).
 */

/** Which bundles (FilterBar's tabs), and the kinds of what needs attention on them (its chips), worst first. */
type Tab = "active" | "archived" | "all";
const TABS: { id: Tab; label: string }[] = [
    { id: "active", label: "Active" },
    { id: "archived", label: "Archived" },
    { id: "all", label: "All" },
];
type Kind = "number" | "low" | "retired" | "items" | "changed" | "names";
const KINDS: { id: Kind; label: string; danger?: boolean }[] = [
    { id: "number", label: "Item number problem", danger: true },
    { id: "low", label: "Making too little", danger: true },
    { id: "retired", label: "Holding archived items" },
    { id: "items", label: "Items needing attention" },
    { id: "changed", label: "Changed in ServiceM8" },
    { id: "names", label: "Name to update" },
];

/** A bundle's row: what it adds up to (bundleTotals.ts), and what needs attention on it (attention.ts). */
interface BundleListRow {
    b: BundleRow;
    totals: BundleTotals;
    problems: string[];
    numberProblem: boolean;
    /** Red: its item number, making too little, or an item in it with something red. */
    danger: boolean;
    low: boolean;
    /** Its items needing attention (attention.ts bundleItemAttention). */
    items: number;
    retired: boolean;
    changed: boolean;
    /** Its name reads differently from what its tags give it. */
    nameToUpdate: boolean;
    /** Its name, item number and its items' (searching an item finds the bundles holding it). */
    searchText: string;
}

/** An item's name and number, for finding the bundles holding it (none: gone). */
const itemWords = (m: MaterialRow | undefined) => (m ? [m.name, m.item_number] : []);

const inTab = (tab: Tab, r: BundleListRow) => tab === "all" || r.b.active === (tab === "active");

/** Whether a bundle has this kind of what needs attention (any: anything at all). */
function hasKind(kind: Kind | typeof ANY_KIND, r: BundleListRow) {
    switch (kind) {
        case "number":
            return r.numberProblem;
        case "low":
            return r.low;
        case "retired":
            return r.retired;
        case "items":
            return r.items > 0;
        case "changed":
            return r.changed;
        case "names":
            return r.b.active && r.nameToUpdate;
        default:
            return r.problems.length > 0;
    }
}

export function BundlesList(props: Readonly<{ data: PriceListData; openUuid?: string; onOpen: (uuid: string) => void; onNew: () => void }>) {
    const { data } = props;
    const seesCosts = can(data, "costs.view");
    // Amounts are in the business's usual terms, labelled by its tax's name ("inc GST").
    const basis = basisLabel(useCompany().money.amountsIncludeTax, rateFor("", data.taxRates));
    // A link can open it already searched (e.g. "See all" from Home's search): ?q=...; or on a filter: ?filter=attention.
    const [query, setQuery] = useState(() => new URLSearchParams(globalThis.location.search).get("q") ?? "");
    const linked = new URLSearchParams(useSearch()).get("filter");
    const [tab, setTab] = useState<Tab>(TABS.some((t) => t.id === linked) ? (linked as Tab) : "active");
    // Links can open it narrowed to what needs attention: ?filter=attention (any of it), or one kind.
    const [kind, setKind] = useState<Kind | typeof ANY_KIND | null>(() => {
        if (linked === "attention") return ANY_KIND;
        return KINDS.find((k) => k.id === linked)?.id ?? null;
    });
    // Narrowed to a tag's bundles (?tag=... links here with one): its counts too.
    const [tagId, setTagId] = useState(() => new URLSearchParams(globalThis.location.search).get("tag") ?? "");
    const tag = data.tags.find((t) => t.id === tagId);
    const [checkedOnly, setCheckedOnly] = useState<CheckedFilter>("");
    const all = useMemo((): BundleListRow[] => {
        // Bundles share ServiceM8's item numbers with items, not products (catalogue.ts).
        const numbers = itemNumberIssues([...data.materials.filter((m) => !isProduct(m)), ...data.bundles]);
        const items = new Map(data.materials.map((m) => [m.uuid, m]));
        const changed = new Set(data.changedInServiceM8.materialbundle);
        const ctx = itemContext(data);
        return data.bundles.map((b) => {
            // Its items' warnings (all of them: anything on an item is something to look at here), and what it makes on them.
            const attention = b.active ? bundleItemAttention(b, ctx, seesCosts, false) : new Map<string, ItemProblem[]>();
            const low = seesCosts && bundleMakesTooLittle(b, ctx);
            const numberProblem = bundleNumberProblem(b, numbers);
            return {
                b,
                totals: bundleTotals(data, b.material_list),
                problems: bundleProblems(b, numbers, items, changed.has(b.uuid), smartName(data, b) !== null, { low, itemsNeedingAttention: attention.size }),
                nameToUpdate: smartName(data, b) !== null,
                numberProblem,
                danger: numberProblem || low || [...attention.values()].some((list) => list.some((p) => p.danger)),
                low,
                items: attention.size,
                retired: b.active && retiredInBundle(b, items).length > 0,
                changed: changed.has(b.uuid),
                searchText: [b.name, b.item_number, ...b.material_list.flatMap((l) => itemWords(items.get(l.uuid)))].join(" "),
            };
        });
    }, [data, seesCosts]);
    // Narrowed to a tag: its counts too. Then the tab, then what needs attention among those.
    const tagged = (tag ? all.filter((r) => (data.itemTags[r.b.uuid] ?? []).includes(tag.id)) : all).filter((r) => inCheckedFilter(checkedOnly, data, r.b.uuid));
    const inTabNow = tagged.filter((r) => inTab(tab, r));
    const kinds = KINDS.filter((k) => seesCosts || k.id !== "low").map((k) => ({ ...k, count: inTabNow.filter((r) => hasKind(k.id, r)).length }));
    const rows = inTabNow.filter((r) => (kind === null || hasKind(kind, r)) && matchesSearch(r.searchText, query));

    return (
        <div className="page is-wide">
            <PageHeader
                title="Bundles"
                icon={<Boxes className="size-6" />}
                tone="copper"
                subtitle={`${plural(data.bundles.filter((b) => b.active).length, "bundle")}: items added to a job together under one line, each at its own price. A bundle's price is its items' added up.`}
                actions={
                    can(data, "bundles") ? (
                        <button type="button" className="btn-primary" onClick={props.onNew}>
                            <Plus className="size-4" /> New bundle
                        </button>
                    ) : null
                }
            />
            {data.bundlesUnavailable ? (
                <div className="mb-4">
                    <Notice>{data.bundlesUnavailable}</Notice>
                </div>
            ) : null}
            <Panel flush>
                <FilterBar
                    searchLabel="Search bundles"
                    placeholder="Search by name or item number, or an item in it"
                    query={query}
                    onQuery={setQuery}
                    tabs={TABS.map((t) => ({ ...t, count: tagged.filter((r) => inTab(t.id, r)).length }))}
                    tab={tab}
                    onTab={setTab}
                    kinds={kinds}
                    anyCount={inTabNow.filter((r) => hasKind(ANY_KIND, r)).length}
                    kind={kind}
                    onKind={setKind}
                >
                    <CheckedOnly value={checkedOnly} onChange={setCheckedOnly} />
                    <TagFilter tags={data.tags} value={tag ? tag.id : ""} onChange={setTagId} />
                </FilterBar>
                <table className={clsx("list is-fixed", seesCosts ? "min-w-[860px]" : "min-w-[560px]")}>
                    {/* The item number and figures get just the room they need; the name gets the rest. */}
                    <colgroup>
                        <col className="w-28" />
                        <col />
                        <col className="w-20" />
                        <col className="w-32" />
                        {seesCosts ? (
                            <>
                                <col className="w-28" />
                                <col className="w-20" />
                                <col className="w-24" />
                            </>
                        ) : null}
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Item #</th>
                            <th>Name</th>
                            <th className="text-right">Items</th>
                            <th className="text-right">Price</th>
                            {/* What the business pays and makes: only with See costs (the server leaves item costs out otherwise). */}
                            {seesCosts ? (
                                <>
                                    <th className="text-right">Cost</th>
                                    <th className="text-right">Markup</th>
                                    <th className="text-right" title="Estimated profit before tax: its items' prices less their costs">
                                        Est. profit
                                    </th>
                                </>
                            ) : null}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <BundleLine key={r.b.uuid} row={r} checked={data.checked[r.b.uuid]} nameTags={data.nameTags} seesCosts={seesCosts} minMarkup={data.settings.minMarkup} basis={basis} open={props.openUuid === r.b.uuid} onOpen={() => props.onOpen(r.b.uuid)} />
                        ))}
                    </tbody>
                </table>
                {rows.length === 0 ? <p className="p-6 text-center text-sm text-muted">{data.bundles.length ? "No bundles match." : "No bundles yet."}</p> : null}
            </Panel>
        </div>
    );
}

/**
 * A bundle's row: its item number, name, how many items, and what they add up to, in the business's usual terms (with tax, or
 * without). Its cost and markup are its items' added up, before tax: it has no pricing of its own (its items do).
 */
function BundleLine(props: Readonly<{ row: BundleListRow; checked: CheckedMark | undefined; nameTags: NameTagConfig; seesCosts: boolean; minMarkup: number; /** How its amounts are taxed ("inc GST"). */ basis: string; open: boolean; onOpen: () => void }>) {
    const { b, totals, problems, numberProblem, danger } = props.row;
    // Tinted while it needs attention: red when any of it is red.
    let rowTone: string | null = null;
    if (problems.length) rowTone = danger ? "is-danger" : "is-warning";
    const fmt = useMoney();
    const { money } = useCompany();
    const price = money.amountsIncludeTax ? totals.priceWithTax : totals.priceNet;
    const label = props.basis;
    // Its cost in the same terms: before tax, with its items' tax on it as on their prices (one blended rate).
    const cost = money.amountsIncludeTax && totals.priceNet > 0 ? totals.costNet * (totals.priceWithTax / totals.priceNet) : totals.costNet;
    const profit = totals.priceNet - totals.costNet;
    // None with an item in it that has no cost: it'd be far too high.
    const markupPct = totals.costNet > 0 && !totals.noCost ? (profit / totals.costNet) * 100 : null;
    return (
        // The name button covers the whole row (after:inset-0): click selects it, double-click opens the bundle.
        <tr className={clsx("relative", rowTone, props.open ? "bg-brand-500/10" : "hover:bg-surface-2 focus-within:bg-brand-500/5")} aria-current={props.open || undefined}>
            <td className="h-14 py-2">
                <ItemNumberCell itemNumber={b.item_number} issue={numberProblem ? (b.item_number.trim() ? "duplicate" : "missing") : null} />
            </td>
            <td className="py-2">
                <button type="button" className="block w-full min-w-0 cursor-default text-left outline-none after:absolute after:inset-0" {...rowOpen(props.onOpen)}>
                    {/* Its name as an item's is shown (its tags' wording coloured: ListName), struck through while archived. */}
                    <ListName
                        name={b.name}
                        tags={props.nameTags}
                        problems={problems}
                        danger={danger}
                        struck={!b.active}
                        mark={<CheckedListMark mark={props.checked} />}
                        untitled="Untitled bundle"
                        under={totals.missing ? <span className="text-danger">{plural(totals.missing, "item")} in it no longer there</span> : undefined}
                    />
                </button>
            </td>
            <td className="text-right tabular-nums">{b.material_list.length.toLocaleString()}</td>
            <td className="text-right">
                <div className="tabular-nums">{fmt.money(price)}</div>
                <div className="text-xs text-muted">{label}</div>
            </td>
            {props.seesCosts ? (
                <>
                    <td className="text-right">
                        <div className="tabular-nums">{fmt.money(cost)}</div>
                        <div className="text-xs text-muted">{label}</div>
                    </td>
                    <td className="text-right tabular-nums">
                        <MarkupCell markupPct={markupPct} low={underMinimum(markupPct, props.minMarkup)} minMarkup={props.minMarkup} />
                    </td>
                    <td className="text-right tabular-nums">
                        <TotalCell total={totals.costNet > 0 ? profit : null} />
                    </td>
                </>
            ) : null}
        </tr>
    );
}
